import type { Ref } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useFetcher } from 'react-router';
import type { CopOrDropSection } from 'types/capi/copOrDropSection';
import type { ProductListItemInternal } from 'types/capi/shared';

import BinIcon from '~/assets/icons/Bin';
import FlameIcon from '~/assets/icons/Flame';
import FlameSparkleIcon from '~/assets/icons/FlameSparkle';
import Image from '~/components/Image';
import CopOrDropEvent from '~/events/CopOrDropEvent';
import useRootData from '~/hooks/useRootData';

type ClickEvent = (btn: 'cop' | 'drop') => void;
type CopOrDropResponse = { are_recommendations_ready: boolean };

function ProductCard({
  data,
  active,
  onClick
}: {
  active: boolean;
  data: ProductListItemInternal;
  onClick: ClickEvent;
}) {
  const imageSize = 768;

  return (
    <div
      className={`flex w-[285px] shrink-0 flex-col gap-2 md:w-[264px] md:gap-4 lg:w-[360px] ${!active ? 'brightness-[25%]' : ''}`}
    >
      <div className="flex aspect-square w-full items-center justify-center bg-white">
        <Image
          alt={data.image.alt}
          src={data.image.url}
          height={imageSize}
          width={imageSize}
          pad
          quality={100}
          responsiveOptions={{
            lg: { height: 768, retainRatio: true },
            sm: { height: 576, retainRatio: true }
          }}
        />
      </div>
      <div className="flex w-full gap-2">
        <button
          onClick={() => onClick('drop')}
          disabled={!active}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-alert px-[10px] py-2 font-bold"
        >
          <BinIcon />
          <span>Drop</span>
        </button>
        <button
          onClick={() => onClick('cop')}
          disabled={!active}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-700 px-[10px] py-2 font-bold"
        >
          <FlameIcon
            fill="none"
            stroke="white"
            height={16}
            width={16}
            strokeWidth={2}
          />
          <span>Cop</span>
        </button>
      </div>
    </div>
  );
}

function EmptyProductCard({ active }: { active: boolean }) {
  return (
    <div
      className={`flex w-[285px] shrink-0 flex-col gap-2 md:w-[264px] md:gap-4 lg:w-[360px] ${!active ? 'brightness-[25%]' : ''}`}
    >
      <div className="flex aspect-square w-full items-center justify-center bg-white"></div>
    </div>
  );
}

function Products({
  items,
  currentIndex,
  setCurrentIndex,
  fetcher
}: {
  currentIndex: number;
  fetcher: ReturnType<typeof useFetcher>;
  items: ProductListItemInternal[];
  setCurrentIndex: React.Dispatch<number>;
}) {
  const { clientBrowser } = useRootData();
  const scrollView: Ref<HTMLDivElement> = useRef(null);
  const { isBot } = useRootData();

  // Ensures the active product is always in view on initial render
  useEffect(() => {
    scrollView.current?.scrollBy({
      behavior: 'auto',
      left: -10000
    });
  }, []);

  useEffect(() => {
    if (!currentIndex) return;

    const target = scrollView.current?.children.item(currentIndex);

    target?.scrollIntoView({
      behavior: clientBrowser !== 'chrome' ? 'smooth' : 'auto',
      block: 'center',
      inline: 'start'
    });
  }, [currentIndex, clientBrowser]);

  const onClick: ClickEvent = (btn) => {
    setCurrentIndex(currentIndex + 1);

    new CopOrDropEvent({
      action: btn,
      isBot,
      productName: items[currentIndex]?.name ?? ''
    }).send();

    fetcher.submit(
      { decision: btn, product_id: items[currentIndex]?.id ?? null },
      {
        action: '/bolt/cod',
        encType: 'application/json',
        method: 'POST'
      }
    );
  };

  return (
    <div
      ref={scrollView}
      className="flex w-full items-start gap-2 self-stretch overflow-x-hidden lg:gap-6"
    >
      {items
        ? items.map((product, productIndex) => (
            <ProductCard
              onClick={onClick}
              key={productIndex}
              data={product}
              active={productIndex === currentIndex}
            />
          ))
        : Array.from({ length: 8 }).map((_, index) => (
            <EmptyProductCard key={index} active={index === 0} />
          ))}
    </div>
  );
}

export default function CopOrDrop() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const postFetcher = useFetcher<CopOrDropResponse>();
  const { data, load } = useFetcher<CopOrDropSection>();

  useEffect(() => {
    load('/bolt/cod');
  }, [load]);

  const shouldShowRecommendations =
    postFetcher.data?.are_recommendations_ready ||
    data?.are_recommendations_ready;

  return (
    <div className="flex w-full items-center justify-center bg-grey-dark text-white">
      <div className="content-width flex w-full flex-col gap-6 py-10 lg:gap-8 lg:py-20">
        <div className="flex flex-col justify-center gap-2">
          <span className="text-title-lg">
            Want personalised recommendations?
          </span>
          <span className="flex gap-2 text-base lg:text-lg">
            <FlameSparkleIcon />
            <span className="flex items-center">
              Cop 6 pairs to get started
            </span>
          </span>
        </div>
        <Products
          fetcher={postFetcher}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          items={data?.candidates as ProductListItemInternal[]}
        />
        {shouldShowRecommendations && (
          <div className="flex w-full items-center justify-center">
            <Link
              to="/recommendations"
              className="flex w-[323px] items-center justify-center gap-2 rounded-lg border border-solid border-white bg-transparent px-[10px] py-2 text-base font-bold leading-6 shadow-sm shadow-white lg:w-[344px]"
            >
              <FlameSparkleIcon width={18} height={18} />
              <span>Your recommendations</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
